import { forwardRef, useCallback, useEffect } from 'react'

export interface SeekLineProps extends React.HTMLAttributes<HTMLDivElement> {
  audioUrl: string
  audioRef: React.RefObject<HTMLAudioElement>
  currentTime: number
  duration: number
  handleLoadedMetadata: () => void
  setCurrentTime: (time: number) => void
  resetPlayer: () => void
}

const SeekLine = forwardRef<HTMLDivElement, SeekLineProps>(
  (
    {
      audioUrl,
      audioRef,
      currentTime,
      duration,
      handleLoadedMetadata,
      setCurrentTime,
      resetPlayer,
    },
    ref,
  ) => {
    const handleSliderChange = (event: React.MouseEvent<HTMLButtonElement>) => {
      const audioElement = audioRef.current
      if (!audioElement) return

      const rect = event.currentTarget.getBoundingClientRect()
      const offsetX = event.clientX - rect.left
      const newTime = (offsetX / rect.width) * duration

      audioElement.currentTime = newTime
      setCurrentTime(newTime)
    }

    const handleTimeChange = useCallback(() => {
      if (!audioRef.current) return
      setCurrentTime(audioRef.current.currentTime)
    }, [audioRef, setCurrentTime])

    // update sleek line position
    useEffect(() => {
      const audioElement = audioRef.current
      if (!audioElement) return
      audioElement.addEventListener('timeupdate', handleTimeChange)
      return () => {
        audioElement.removeEventListener('timeupdate', handleTimeChange)
      }
    }, [audioRef, handleTimeChange])

    useEffect(() => {
      if (currentTime === 0) return
      if (Math.round(currentTime) === Math.round(duration)) {
        // 1 second delay to allow the audio to finish playing and for smooth transition
        setTimeout(() => {
          resetPlayer()
        }, 1000)
      }
    }, [currentTime, duration, resetPlayer])

    const progress = duration > 0 ? Math.max(0, Math.min(100, (currentTime / duration) * 100)) : 0

    return (
      <div ref={ref}>
        <div className="group mr-2 ml-1">
          <button
            className="relative flex items-center w-full h-2 mb-1 bg-st2 rounded-lg cursor-pointer pl-1 focus-visible"
            onClick={handleSliderChange}
            aria-label="Seek line"
            data-testid="button-audio-seek"
            type="button"
          >
            <div
              className="absolute left-0 bg-primary rounded-lg h-full transition-all duration-500 ease-linear"
              style={{ width: `${progress}%` }}
            >
              <div
                className="w-4 h-4 bg-primary rounded-lg block cursor-pointer border-none opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                style={{
                  right: '-5px',
                  position: 'absolute',
                  top: '-4px',
                }}
              />
            </div>
          </button>
        </div>

        <audio
          ref={audioRef}
          className="hidden"
          src={audioUrl}
          preload="metadata"
          onLoadedMetadata={handleLoadedMetadata}
        />
      </div>
    )
  },
)
SeekLine.displayName = 'SeekLine'

export { SeekLine }
